import { useEffect, useMemo, useRef, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogHeaderImage } from 'foundations-components/transitional/components/DialogHeaderImage';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { proDefaultSurveyResponseMap } from '../../../constants/trial';
import { getTrackingProperties, track } from '../../../utils/usageTracker';
import { FeatureTile } from './ProfessionalFeatureSurvey/FeatureTile';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ProfessionalFeatureSurvey = ({
  apiName,
  trackingData,
  trialTrackingProperties,
  upgradeConfig,
  onSurveySubmit = () => {}
}) => {
  const lastApiName = useRef(apiName);
  const [allFeatures, setSelectedFeatures] = useState(proDefaultSurveyResponseMap[apiName]);
  const featureData = Object.values(allFeatures);
  const isDisabled = featureData.every(({
    isSelected
  }) => !isSelected);
  const trackingProperties = useMemo(() => getTrackingProperties({
    apiName,
    trackingData,
    upgradeConfig
  }), [apiName, trackingData, upgradeConfig]);
  useEffect(() => {
    track('trialActivationInteraction', Object.assign({
      action: 'viewed pricing page survey modal'
    }, trackingProperties, trialTrackingProperties));
  }, [trackingProperties, trialTrackingProperties]);
  useEffect(() => {
    if (lastApiName.current !== apiName) {
      setSelectedFeatures(() => proDefaultSurveyResponseMap[apiName]);
      lastApiName.current = apiName;
    }
  }, [apiName]);
  const handleSubmitSelectedFeatures = () => {
    track('trialActivationInteraction', Object.assign({
      action: 'viewed pricing page survey modal'
    }, trackingProperties, trialTrackingProperties));
    onSurveySubmit();
  };
  const handleSkip = () => {
    track('trialActivationInteraction', Object.assign({
      action: 'survey skipped'
    }, trackingProperties, trialTrackingProperties));
    onSurveySubmit();
  };
  const handleSelectedFeatureChange = (featureKey, isSelected) => {
    const newSelectedFeatures = Object.assign({}, allFeatures, {
      [featureKey]: Object.assign({}, allFeatures[featureKey], {
        isSelected
      })
    });
    setSelectedFeatures(newSelectedFeatures);
  };
  return /*#__PURE__*/_jsxs(Modal, {
    use: "success",
    width: 750,
    size: "auto",
    centered: true,
    children: [/*#__PURE__*/_jsx(DialogHeaderImage, {
      offsetTop: 40,
      offsetBottom: -30,
      children: /*#__PURE__*/_jsx(Illustration, {
        name: "success",
        width: 118
      })
    }), /*#__PURE__*/_jsx(DialogHeader, {
      children: /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        children: /*#__PURE__*/_jsx(Text, {
          as: "h2",
          textAlign: "center",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "purchaseMotions.trial.featureSurvey.trialTitle"
          })
        })
      })
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "p",
        typography: "heading-50",
        textAlign: "center",
        marginBlockEnd: "400",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "purchaseMotions.trial.featureSurvey.headline"
        })
      }), /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        flexDirection: "row",
        justifyContent: "center",
        gap: "600",
        flexWrap: "wrap",
        marginBlockStart: "200",
        children: featureData.map(surveyFeature => /*#__PURE__*/_jsx(FeatureTile, {
          feature: surveyFeature,
          onSelectionChange: handleSelectedFeatureChange
        }, surveyFeature.featureKey))
      })]
    }), /*#__PURE__*/_jsx(DialogFooter, {
      align: "center",
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        flexDirection: "column",
        gap: "300",
        marginBlockStart: "200",
        children: [/*#__PURE__*/_jsx(Tooltip, {
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "purchaseMotions.trial.featureSurvey.tooltip"
          }),
          disabled: !isDisabled,
          children: /*#__PURE__*/_jsx(Button, {
            use: "primary",
            onClick: () => {
              handleSubmitSelectedFeatures();
            },
            disabled: isDisabled,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "purchaseMotions.trial.featureSurvey.submitButton"
            })
          })
        }), /*#__PURE__*/_jsx(Button, {
          margin: "0",
          use: "link",
          onClick: handleSkip,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "purchaseMotions.trial.featureSurvey.skipButton"
          })
        })]
      })
    })]
  });
};